import { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { AttachmentGallery } from "../portal/AttachmentGallery";
import { renderWithMentions } from "./useMentions";
import { fmtTime, stripClient, sourceLabel } from "./threadUtils";
import { LockKey, UsersThree, PencilSimple, Trash, ArrowBendUpLeft, UserCircle, Buildings, Check, Checks } from "@phosphor-icons/react";

const TONES = {
  internal: "border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/30",
  shared: "border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/25",
  client: "border-border bg-muted/60",
  mine: "border-primary/25 bg-primary/10",
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/* WhatsApp-style receipt: one red tick = sent, two amber = received (other side notified), two green = read. */
export function Ticks({ receipt, sentAt, testId }) {
  if (!receipt) return null;
  const { status, delivered_at, read_by = [] } = receipt;
  const cfg = {
    sent: { Icon: Check, cls: "text-rose-500", label: `Sent ${fmtWhen(sentAt)}` },
    delivered: { Icon: Checks, cls: "text-amber-500", label: `Received — notified ${fmtWhen(delivered_at)}` },
    read: { Icon: Checks, cls: "text-emerald-500", label: `Read by ${read_by.map((r) => `${r.name} ${fmtWhen(r.at)}`).join(", ")}` },
  }[status] || null;
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center ${cfg.cls}`} title={cfg.label} aria-label={cfg.label} data-testid={testId} data-status={status}>
      <cfg.Icon size={14} weight="bold" />
    </span>
  );
}

function Header({ item, viewer, mine, teamName, myUserId, testPrefix }) {
  const isClient = item.user_role === "client";
  const name = stripClient(item.user_name);
  const you = String(item.user_id || "") === String(myUserId || "");
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-1 text-[11px]">
      {isClient ? <UserCircle size={13} weight="duotone" className="text-primary shrink-0" /> : <Buildings size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
      <span className="font-semibold">{you ? "You" : name}</span>
      {isClient && viewer === "admin" && <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-primary/15 text-primary" data-testid={`${testPrefix}-client-badge-${item.id}`}>Client</span>}
      {!isClient && viewer === "client" && teamName && <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-muted text-muted-foreground">{teamName}</span>}
      {!isClient && viewer === "admin" && item.user_role && <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{item.user_role.replace("_", " ")}</span>}
      {item.edited && <span className="text-[9px] text-muted-foreground italic" data-testid={`${testPrefix}-edited-${item.id}`}>edited</span>}
      <span className="text-[10px] text-muted-foreground ml-auto inline-flex items-center gap-1">
        {fmtTime(item.created_at)}
        {mine && <Ticks receipt={item.receipt} sentAt={item.created_at} testId={`${testPrefix}-ticks-${item.id}`} />}
      </span>
    </div>
  );
}

function AdminFooter({ item, uid, onToggleShare, onEdit, onDelete, testPrefix }) {
  const isClient = item.user_role === "client";
  const shared = !!item.visible_to_client;
  return (
    <div className="flex items-center gap-2 mt-2 pt-1.5 border-t border-black/5 dark:border-white/5 text-[10px]">
      {isClient ? (
        <span className="flex items-center gap-1 text-primary font-medium"><UsersThree size={11} /> Visible to everyone</span>
      ) : (
        <>
          <span className={`flex items-center gap-1 font-medium ${shared ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`} data-testid={`${testPrefix}-visibility-${item.id}`}>
            {shared ? <><UsersThree size={11} weight="bold" /> Shared with client</> : <><LockKey size={11} weight="bold" /> Internal</>}
          </span>
          {onToggleShare && (
            <button type="button" onClick={() => onToggleShare(item)} className="underline-offset-2 hover:underline text-muted-foreground hover:text-foreground" data-testid={`${testPrefix}-share-${item.id}`}>
              {shared ? "Make internal" : "Share with client"}
            </button>
          )}
        </>
      )}
      <span className="ml-auto flex items-center gap-0.5">
        {!isClient && String(item.user_id) === uid && onEdit && (
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => onEdit()} data-testid={`${testPrefix}-edit-${item.id}`}><PencilSimple size={11} /></Button>
        )}
        {onDelete && (
          <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={() => onDelete(item)} data-testid={`${testPrefix}-delete-${item.id}`}><Trash size={11} /></Button>
        )}
      </span>
    </div>
  );
}

/* One message in the thread. `viewer` = "admin" | "client"; `mine` puts the bubble on the right. */
export function ChatBubble({ item, mine, viewer = "admin", teamName = "", myUserId = "", onToggleShare, onEdit, onDelete, testPrefix = "note" }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.text || "");
  const isClient = item.user_role === "client";
  const tone = viewer === "admin" ? (isClient ? TONES.client : item.visible_to_client ? TONES.shared : TONES.internal) : (mine ? TONES.mine : TONES.client);
  const src = sourceLabel(item.source);

  const saveEdit = async () => {
    if (!editText.trim()) return;
    await onEdit(item, editText.trim());
    setEditing(false);
  };

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`${testPrefix}-${item.id}`}>
      <div className={`w-fit max-w-[92%] sm:max-w-[78%] rounded-2xl ${mine ? "rounded-tr-md" : "rounded-tl-md"} border px-3.5 py-2.5 shadow-sm ${tone}`}>
        {src && (
          <p className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1" data-testid={`${testPrefix}-source-${item.id}`}>
            <ArrowBendUpLeft size={10} /> {src}
          </p>
        )}
        <Header item={item} viewer={viewer} mine={mine} teamName={teamName} myUserId={myUserId} testPrefix={testPrefix} />
        {editing ? (
          <div>
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="mb-2 text-sm bg-background" data-testid={`${testPrefix}-edit-input`} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="h-6 text-xs" onClick={saveEdit} data-testid={`${testPrefix}-save`}>Save</Button>
            </div>
          </div>
        ) : (
          item.text && <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{viewer === "admin" ? renderWithMentions(item.text) : item.text}</p>
        )}
        {(item.attachments || []).length > 0 && (
          <div className="mt-2" data-testid={`${testPrefix}-files-${item.id}`}>
            <AttachmentGallery attachments={item.attachments} testid={`${testPrefix}-gallery-${item.id}`} />
          </div>
        )}
        {viewer === "admin" && <AdminFooter item={item} uid={String(myUserId)} onToggleShare={onToggleShare} onEdit={onEdit ? () => setEditing(true) : null} onDelete={onDelete} testPrefix={testPrefix} />}
      </div>
    </div>
  );
}
